import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  type CreateCompanyData,
  type UpdateCompanyData,
  type Company,
} from "../services/companyApi";
import { companySchema } from "@repo/schemas/companySchema";
import { canadianProvinces } from "@repo/schemas/addressSchema";

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyFormProps {
  company?: Company;
  onSubmit: (data: CreateCompanyData | UpdateCompanyData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function CompanyForm({
  company,
  onSubmit,
  onCancel,
  isLoading,
}: CompanyFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: company?.name || "",
      email: company?.email || "",
      phone: company?.phone || "",
      greetOnConnectEnabled: company?.greetOnConnectEnabled ?? true,
      address: {
        line1: company?.address?.line1 || "",
        line2: company?.address?.line2 || "",
        city: company?.address?.city || "",
        state: company?.address?.state || "",
        postalCode: company?.address?.postalCode || "",
        country: company?.address?.country || "CA",
      },
    },
  });

  // Watch address line1 to determine if address is being filled
  const addressLine1 = watch("address.line1");

  // Reset form when company prop changes
  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        email: company.email || "",
        phone: company.phone || "",
        greetOnConnectEnabled: company.greetOnConnectEnabled ?? true,
        address: company.address
          ? {
              line1: company.address.line1,
              line2: company.address.line2 || "",
              city: company.address.city,
              state: company.address.state,
              postalCode: company.address.postalCode,
              country: company.address.country || "CA",
            }
          : {
              line1: "",
              line2: "",
              city: "",
              state: "",
              postalCode: "",
              country: "CA",
            },
      });
    }
  }, [company, reset]);

  const onFormSubmit = (data: CompanyFormData) => {
    const submitData = {
      ...data,
      // Only include address if line1 is provided, and cast to proper type
      address: data.address?.line1
        ? {
            line1: data.address.line1,
            line2: data.address.line2,
            city: data.address.city!,
            state: data.address.state!,
            postalCode: data.address.postalCode!,
            country: data.address.country,
          }
        : undefined,
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Company Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Company Information</h3>

        <div className="space-y-2">
          <Label htmlFor="name">Company Name *</Label>
          <Input
            id="name"
            {...register("name")}
            placeholder="Acme Corporation"
          />
          {errors.name && (
            <p className="text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              placeholder="contact@acme.com"
            />
            {errors.email && (
              <p className="text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="tel"
              {...register("phone")}
              placeholder="+1 (555) 123-4567"
            />
            {errors.phone && (
              <p className="text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="greetOnConnectEnabled"
            checked={getValues("greetOnConnectEnabled")}
            onCheckedChange={(checked) =>
              setValue("greetOnConnectEnabled", checked === true)
            }
          />
          <Label
            htmlFor="greetOnConnectEnabled"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Enable greeting on connect
          </Label>
        </div>
      </div>

      <Separator />

      {/* Address Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Address Information (Optional)</h3>

        <div className="space-y-2">
          <Label htmlFor="address.line1">Address Line 1</Label>
          <Input
            id="address.line1"
            {...register("address.line1")}
            placeholder="123 Main Street"
          />
          {errors.address?.line1 && (
            <p className="text-sm text-red-600">
              {errors.address.line1.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="address.line2">Address Line 2</Label>
          <Input
            id="address.line2"
            {...register("address.line2")}
            placeholder="Suite 100"
          />
          {errors.address?.line2 && (
            <p className="text-sm text-red-600">
              {errors.address.line2.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="address.city">City</Label>
            <Input
              id="address.city"
              {...register("address.city")}
              placeholder="Toronto"
              disabled={!addressLine1}
            />
            {errors.address?.city && (
              <p className="text-sm text-red-600">
                {errors.address.city.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address.state">Province</Label>
            <Select
              disabled={!addressLine1}
              onValueChange={(value) => setValue("address.state", value)}
              value={getValues("address.state") || ""}
            >
              <SelectTrigger id="address.state" className="w-full">
                <SelectValue placeholder="Select province" />
              </SelectTrigger>
              <SelectContent>
                {canadianProvinces.map((province) => (
                  <SelectItem key={province.code} value={province.code}>
                    {province.name} ({province.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.address?.state && (
              <p className="text-sm text-red-600">
                {errors.address.state.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address.postalCode">Postal Code</Label>
            <Input
              id="address.postalCode"
              {...register("address.postalCode")}
              placeholder="M5V 3L9"
              disabled={!addressLine1}
            />
            {errors.address?.postalCode && (
              <p className="text-sm text-red-600">
                {errors.address.postalCode.message}
              </p>
            )}
          </div>
        </div>

        {/* Display general address validation error */}
        {errors.address && typeof errors.address.message === "string" && (
          <p className="text-sm text-red-600">{errors.address.message}</p>
        )}
      </div>

      <div className="flex justify-end space-x-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading
            ? "Saving..."
            : company
            ? "Update Company"
            : "Create Company"}
        </Button>
      </div>
    </form>
  );
}
